import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Loader2,
  FileText,
  Plus,
  Edit,
  Trash2,
  Eye,
  MoreVertical,
  Shield,
  Video,
  Calendar,
  AlertCircle,
  Star,
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import {
  consentService,
  ConsentTemplate,
  CreateConsentTemplateRequest,
  UpdateConsentTemplateRequest,
} from '@/services/consentService';
import ConsentFormEditor from './ConsentFormEditor';
import ConsentFormPreview from './ConsentFormPreview';
import { useClinic } from '@/contexts/ClinicContext';
import ConfirmDeleteDialog from '@/components/ui/confirm-delete-dialog';

type ConfirmAction = 'delete' | 'deactivate' | 'activate' | null;

const FORM_TYPE_LABELS: Record<string, { label: string; icon: React.ReactNode; color: string }> = {
  telehealth: {
    label: 'Telehealth',
    icon: <Video className='h-3 w-3' />,
    color: 'bg-blue-100 text-blue-800',
  },
  hipaa: {
    label: 'HIPAA',
    icon: <Shield className='h-3 w-3' />,
    color: 'bg-purple-100 text-purple-800',
  },
  financial: {
    label: 'Financial',
    icon: <FileText className='h-3 w-3' />,
    color: 'bg-green-100 text-green-800',
  },
  treatment: {
    label: 'Treatment',
    icon: <FileText className='h-3 w-3' />,
    color: 'bg-orange-100 text-orange-800',
  },
  custom: {
    label: 'Custom',
    icon: <FileText className='h-3 w-3' />,
    color: 'bg-gray-100 text-gray-800',
  },
};

const ConsentFormManager: React.FC = () => {
  const { clinic, isLoading: isClinicLoading } = useClinic();
  const [templates, setTemplates] = useState<ConsentTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingTemplate, setEditingTemplate] = useState<ConsentTemplate | null>(null);
  const [previewTemplate, setPreviewTemplate] = useState<ConsentTemplate | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [showEditor, setShowEditor] = useState(false);
  const { toast } = useToast();

  // Confirmation dialog state
  const [confirmAction, setConfirmAction] = useState<ConfirmAction>(null);
  const [confirmTemplate, setConfirmTemplate] = useState<ConsentTemplate | null>(null);
  const [confirmLoading, setConfirmLoading] = useState(false);

  const clinicId = clinic?.id;

  useEffect(() => {
    if (clinicId) {
      fetchTemplates();
    } else if (!isClinicLoading) {
      setLoading(false);
    }
  }, [clinicId, isClinicLoading]);

  const fetchTemplates = async () => {
    if (!clinicId) return;
    try {
      setLoading(true);
      const response = await consentService.getTemplates(clinicId, { includeInactive: true });

      if (response.success) {
        setTemplates(response.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch consent templates:', error);
      toast({
        title: 'Error',
        description: 'Failed to load consent templates',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTemplate = () => {
    setEditingTemplate(null);
    setIsCreating(true);
    setShowEditor(true);
  };

  const handleEditTemplate = (template: ConsentTemplate) => {
    setEditingTemplate(template);
    setIsCreating(false);
    setShowEditor(true);
  };

  const handleSaveTemplate = async (templateData: Partial<ConsentTemplate>) => {
    if (!clinicId) return;
    try {
      if (isCreating) {
        await consentService.createTemplate(clinicId, templateData as CreateConsentTemplateRequest);
        toast({
          title: 'Template Created',
          description: 'Consent form template created successfully',
        });
      } else if (editingTemplate) {
        await consentService.updateTemplate(
          clinicId,
          editingTemplate.id,
          templateData as UpdateConsentTemplateRequest
        );
        toast({
          title: 'Template Updated',
          description: 'Consent form template updated successfully',
        });
      }

      setShowEditor(false);
      setEditingTemplate(null);
      setIsCreating(false);
      fetchTemplates();
    } catch (error: any) {
      console.error('Failed to save template:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to save template',
        variant: 'destructive',
      });
    }
  };

  // Show confirmation dialog for delete
  const handleDeleteTemplate = (template: ConsentTemplate) => {
    if (template.is_default) {
      toast({
        title: 'Cannot Delete',
        description: 'Default templates cannot be deleted',
        variant: 'destructive',
      });
      return;
    }
    setConfirmTemplate(template);
    setConfirmAction('delete');
  };

  // Show confirmation dialog for activate/deactivate
  const handleToggleActive = (template: ConsentTemplate) => {
    setConfirmTemplate(template);
    setConfirmAction(template.is_active ? 'deactivate' : 'activate');
  };

  // Execute the confirmed action
  const executeConfirmedAction = async () => {
    if (!clinicId || !confirmTemplate || !confirmAction) return;

    setConfirmLoading(true);
    try {
      if (confirmAction === 'delete') {
        await consentService.deleteTemplate(clinicId, confirmTemplate.id);
        toast({
          title: 'Template Deleted',
          description: 'Consent form template deleted successfully',
        });
      } else if (confirmAction === 'deactivate') {
        await consentService.toggleTemplateActive(clinicId, confirmTemplate.id, false);
        toast({
          title: 'Template Deactivated',
          description: 'Template is now inactive',
        });
      } else if (confirmAction === 'activate') {
        await consentService.toggleTemplateActive(clinicId, confirmTemplate.id, true);
        toast({
          title: 'Template Activated',
          description: 'Template is now active',
        });
      }
      fetchTemplates();
    } catch (error: any) {
      console.error(`Failed to ${confirmAction} template:`, error);
      toast({
        title: 'Error',
        description: error.message || `Failed to ${confirmAction} template`,
        variant: 'destructive',
      });
      throw error; // Re-throw to let dialog handle it
    } finally {
      setConfirmLoading(false);
      setConfirmAction(null);
      setConfirmTemplate(null);
    }
  };

  // Get confirmation dialog props based on action type
  const getConfirmDialogProps = () => {
    if (!confirmTemplate || !confirmAction) return null;

    switch (confirmAction) {
      case 'delete':
        return {
          title: 'Delete Consent Template',
          description: `Are you sure you want to delete "${confirmTemplate.name}"? This template will be permanently removed and cannot be recovered.`,
          actionText: 'Delete',
          actionType: 'delete' as const,
          successMessage: 'Template deleted successfully',
        };
      case 'deactivate':
        return {
          title: 'Deactivate Consent Template',
          description: `Are you sure you want to deactivate "${confirmTemplate.name}"? Patients will no longer be able to sign this template until it is reactivated.`,
          actionText: 'Deactivate',
          actionType: 'warning' as const,
          successMessage: 'Template deactivated successfully',
        };
      case 'activate':
        return {
          title: 'Activate Consent Template',
          description: `Are you sure you want to activate "${confirmTemplate.name}"? This template will be available for patients to sign.`,
          actionText: 'Activate',
          actionType: 'warning' as const,
          successMessage: 'Template activated successfully',
        };
      default:
        return null;
    }
  };

  const handleToggleRequirement = async (
    template: ConsentTemplate,
    field: 'is_required_for_booking' | 'is_required_for_telehealth'
  ) => {
    if (!clinicId) return;
    try {
      if (field === 'is_required_for_booking') {
        await consentService.toggleBookingRequirement(clinicId, template.id, !template[field]);
      } else {
        await consentService.toggleTelehealthRequirement(clinicId, template.id, !template[field]);
      }
      toast({
        title: 'Requirement Updated',
        description: 'Template requirements updated successfully',
      });
      fetchTemplates();
    } catch (error: any) {
      console.error('Failed to update requirement:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to update requirement',
        variant: 'destructive',
      });
    }
  };

  const handleToggleDefault = async (template: ConsentTemplate) => {
    if (!clinicId) return;
    try {
      await consentService.toggleDefaultStatus(clinicId, template.id, !template.is_default);
      toast({
        title: template.is_default ? 'Default Removed' : 'Marked as Default',
        description: template.is_default
          ? 'Template is no longer marked as default'
          : 'Template is now marked as default',
      });
      fetchTemplates();
    } catch (error: any) {
      console.error('Failed to toggle default status:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to update default status',
        variant: 'destructive',
      });
    }
  };

  // Show loading state while clinic context is loading
  if (isClinicLoading || loading) {
    return (
      <div className='flex items-center justify-center p-8'>
        <Loader2 className='h-6 w-6 animate-spin text-muted-foreground' />
      </div>
    );
  }

  // Show message if no clinic is selected
  if (!clinicId) {
    return (
      <Card>
        <CardContent className='flex flex-col items-center justify-center py-12'>
          <AlertCircle className='mb-4 h-12 w-12 text-muted-foreground' />
          <h3 className='mb-2 text-lg font-medium'>No Clinic Selected</h3>
          <p className='text-center text-muted-foreground'>
            Please select a clinic to manage consent forms.
          </p>
        </CardContent>
      </Card>
    );
  }

  // Filter out deleted templates (safety check - backend also filters these)
  const nonDeletedTemplates = templates.filter(t => !t.is_deleted);
  const activeTemplates = nonDeletedTemplates.filter(t => t.is_active);
  const inactiveTemplates = nonDeletedTemplates.filter(t => !t.is_active);

  return (
    <div className='space-y-6'>
      {/* Header Actions */}
      <div className='flex items-center justify-between'>
        <div>
          <p className='text-sm text-muted-foreground'>
            {activeTemplates.length} active templates, {inactiveTemplates.length} inactive
          </p>
        </div>
        <Button onClick={handleCreateTemplate}>
          <Plus className='mr-2 h-4 w-4' />
          Create Template
        </Button>
      </div>

      {/* Info Alert */}
      <Alert>
        <AlertCircle className='h-4 w-4' />
        <AlertDescription>
          <strong>Required Consents:</strong> Mark templates as required for booking or telehealth
          to ensure patients sign them before their appointments. Patients will be prompted to sign
          any missing required consents.
        </AlertDescription>
      </Alert>

      {/* Active Templates */}
      {activeTemplates.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Active Consent Forms</CardTitle>
            <CardDescription>
              These consent forms are available for patients to sign.
            </CardDescription>
          </CardHeader>
          <CardContent className='space-y-4'>
            {activeTemplates.map(template => (
              <TemplateCard
                key={template.id}
                template={template}
                onEdit={() => handleEditTemplate(template)}
                onDelete={() => handleDeleteTemplate(template)}
                onPreview={() => setPreviewTemplate(template)}
                onToggleActive={() => handleToggleActive(template)}
                onToggleRequirement={handleToggleRequirement}
                onToggleDefault={() => handleToggleDefault(template)}
              />
            ))}
          </CardContent>
        </Card>
      )}

      {/* Inactive Templates */}
      {inactiveTemplates.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className='text-muted-foreground'>Inactive Templates</CardTitle>
            <CardDescription>
              These templates are not currently available for patients.
            </CardDescription>
          </CardHeader>
          <CardContent className='space-y-4'>
            {inactiveTemplates.map(template => (
              <TemplateCard
                key={template.id}
                template={template}
                onEdit={() => handleEditTemplate(template)}
                onDelete={() => handleDeleteTemplate(template)}
                onPreview={() => setPreviewTemplate(template)}
                onToggleActive={() => handleToggleActive(template)}
                onToggleRequirement={handleToggleRequirement}
                onToggleDefault={() => handleToggleDefault(template)}
                inactive
              />
            ))}
          </CardContent>
        </Card>
      )}

      {/* Empty State */}
      {templates.length === 0 && (
        <Card>
          <CardContent className='flex flex-col items-center justify-center py-12'>
            <FileText className='mb-4 h-12 w-12 text-muted-foreground' />
            <h3 className='mb-2 text-lg font-medium'>No Consent Forms</h3>
            <p className='mb-4 text-center text-muted-foreground'>
              Create consent form templates for patients to sign before appointments.
            </p>
            <Button onClick={handleCreateTemplate}>
              <Plus className='mr-2 h-4 w-4' />
              Create First Template
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Editor Dialog */}
      <Dialog open={showEditor} onOpenChange={setShowEditor}>
        <DialogContent className='max-h-[90vh] max-w-4xl overflow-y-auto'>
          <DialogHeader>
            <DialogTitle>{isCreating ? 'Create Consent Form' : 'Edit Consent Form'}</DialogTitle>
            <DialogDescription>
              {isCreating
                ? 'Create a new consent form template for patients to sign.'
                : 'Edit the consent form template. Changes to content will create a new version.'}
            </DialogDescription>
          </DialogHeader>
          <ConsentFormEditor
            template={editingTemplate}
            onSave={handleSaveTemplate}
            onCancel={() => setShowEditor(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={!!previewTemplate} onOpenChange={() => setPreviewTemplate(null)}>
        <DialogContent className='max-h-[90vh] max-w-3xl overflow-y-auto'>
          <DialogHeader>
            <DialogTitle>Preview: {previewTemplate?.name}</DialogTitle>
          </DialogHeader>
          {previewTemplate && <ConsentFormPreview template={previewTemplate} />}
        </DialogContent>
      </Dialog>

      {/* Confirmation Dialog */}
      {confirmAction && confirmTemplate && getConfirmDialogProps() && (
        <ConfirmDeleteDialog
          open={!!confirmAction}
          onOpenChange={(open) => {
            if (!open) {
              setConfirmAction(null);
              setConfirmTemplate(null);
            }
          }}
          title={getConfirmDialogProps()!.title}
          description={getConfirmDialogProps()!.description}
          actionText={getConfirmDialogProps()!.actionText}
          actionType={getConfirmDialogProps()!.actionType}
          itemName={confirmTemplate.name}
          itemType="consent template"
          onConfirm={executeConfirmedAction}
          isLoading={confirmLoading}
          successMessage={getConfirmDialogProps()!.successMessage}
        />
      )}
    </div>
  );
};

interface TemplateCardProps {
  template: ConsentTemplate;
  onEdit: () => void;
  onDelete: () => void;
  onPreview: () => void;
  onToggleActive: () => void;
  onToggleRequirement: (
    template: ConsentTemplate,
    field: 'is_required_for_booking' | 'is_required_for_telehealth'
  ) => void;
  onToggleDefault: () => void;
  inactive?: boolean;
}

const TemplateCard: React.FC<TemplateCardProps> = ({
  template,
  onEdit,
  onDelete,
  onPreview,
  onToggleActive,
  onToggleRequirement,
  onToggleDefault,
  inactive,
}) => {
  const typeInfo = FORM_TYPE_LABELS[template.form_type] || FORM_TYPE_LABELS.custom;

  return (
    <div
      className={`flex items-start justify-between rounded-lg border p-4 ${
        inactive ? 'bg-muted/50 opacity-60' : ''
      }`}
    >
      <div className='flex-1'>
        <div className='mb-2 flex items-center gap-2'>
          <h4 className='font-medium'>{template.name}</h4>
          <Badge variant='outline' className={typeInfo.color}>
            {typeInfo.icon}
            <span className='ml-1'>{typeInfo.label}</span>
          </Badge>
          {template.is_default && <Badge variant='secondary'>Default</Badge>}
          {template.version > 1 && <Badge variant='outline'>v{template.version}</Badge>}
        </div>

        {template.description && (
          <p className='mb-3 text-sm text-muted-foreground'>{template.description}</p>
        )}

        <div className='flex flex-wrap gap-4 text-sm'>
          {/* Default toggle - commented out for now
          <div className='flex items-center gap-2'>
            <Star className='h-4 w-4 text-muted-foreground' />
            <Label htmlFor={`default-${template.id}`} className='text-muted-foreground'>
              Default
            </Label>
            <Switch
              id={`default-${template.id}`}
              checked={template.is_default}
              onCheckedChange={onToggleDefault}
              disabled={inactive}
            />
          </div>
          */}
          <div className='flex items-center gap-2'>
            <Calendar className='h-4 w-4 text-muted-foreground' />
            <Label htmlFor={`booking-${template.id}`} className='text-muted-foreground'>
              Required for Booking
            </Label>
            <Switch
              id={`booking-${template.id}`}
              checked={template.is_required_for_booking}
              onCheckedChange={() => onToggleRequirement(template, 'is_required_for_booking')}
              disabled={inactive}
            />
          </div>
          <div className='flex items-center gap-2'>
            <Video className='h-4 w-4 text-muted-foreground' />
            <Label htmlFor={`telehealth-${template.id}`} className='text-muted-foreground'>
              Required for Telehealth
            </Label>
            <Switch
              id={`telehealth-${template.id}`}
              checked={template.is_required_for_telehealth}
              onCheckedChange={() => onToggleRequirement(template, 'is_required_for_telehealth')}
              disabled={inactive}
            />
          </div>
        </div>
      </div>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant='ghost' size='sm'>
            <MoreVertical className='h-4 w-4' />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align='end'>
          <DropdownMenuItem onClick={onPreview}>
            <Eye className='mr-2 h-4 w-4' />
            Preview
          </DropdownMenuItem>
          <DropdownMenuItem onClick={onEdit}>
            <Edit className='mr-2 h-4 w-4' />
            Edit
          </DropdownMenuItem>
          <DropdownMenuItem onClick={onToggleActive}>
            {inactive ? (
              <>
                <Plus className='mr-2 h-4 w-4' />
                Activate
              </>
            ) : (
              <>
                <AlertCircle className='mr-2 h-4 w-4' />
                Deactivate
              </>
            )}
          </DropdownMenuItem>
          {!template.is_default && (
            <DropdownMenuItem onClick={onDelete} className='text-destructive'>
              <Trash2 className='mr-2 h-4 w-4' />
              Delete
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

export default ConsentFormManager;
